import { useMemo } from "react";
import {
  buildCalendarRangeInsightsInput,
  getCalendarInsightsExercises,
} from "../utils/inputs/calendarInsights";
import { dateRangeInsightsStorageId } from "../utils/inputs/buildDateRangeInsightsInput";

/** Function to create a cache key for the active exercises. */
function exercisesCacheKey(activeExercises) {
  if (!Array.isArray(activeExercises) || activeExercises.length === 0) {
    return "";
  }

  return activeExercises.map((exercise) => exercise._id).join(",");
}

/**
 * Memoized Program Insights config for calendar date-range hosts.
 * Pass stable `periodKeys` (e.g. joined periodKey strings) so visiblePeriods
 * array identity does not bust the cache every render.
 *
 * @param {Object} params
 * @param {string} params.scope — `calendar-month` | `calendar-week`
 * @param {boolean} [params.enabled]
 * @param {string} params.from
 * @param {string} params.to
 * @param {string} params.periodKeys — stable signature of visible week columns
 * @param {{ periodKey: string, label: string }[]} params.visiblePeriods
 * @param {Object[]} [params.activeExercises]
 * @param {string} params.athleteId
 * @param {number} [params.startingCalendarDay]
 */
export function useCalendarInsights({
  scope,
  enabled = true,
  from,
  to,
  periodKeys,
  visiblePeriods,
  activeExercises = [],
  athleteId,
  startingCalendarDay = 0,
}) {
  const exercisesKey = exercisesCacheKey(activeExercises);

  return useMemo(() => {
    if (
      !enabled ||
      !from ||
      !to ||
      !athleteId ||
      !visiblePeriods?.length
    ) {
      return {
        insightsInput: null,
        storageId: null,
        insightsActive: false,
      };
    }

    const exercises = getCalendarInsightsExercises(activeExercises, { from, to });
    const insightsInput = buildCalendarRangeInsightsInput({
      exercises,
      from,
      to,
      startingCalendarDay,
      visiblePeriods,
    });

    const storageId = insightsInput
      ? dateRangeInsightsStorageId(scope, athleteId, insightsInput.from, insightsInput.to)
      : null;

    return {
      insightsInput,
      storageId,
      insightsActive: Boolean(insightsInput),
    };
  }, [
    scope,
    enabled,
    from,
    to,
    periodKeys,
    visiblePeriods,
    exercisesKey,
    athleteId,
    startingCalendarDay,
  ]);
}
